import { useEffect, useState } from "react";
import { Calculator, Send, MapPin, Mail, ArrowRight } from "lucide-react";
import axios from "axios";
import { useSEO } from "../hooks/useSEO";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SACHET_VOLUME = 0.12; // 1 sachet 120L = 0,12 m³

function SurfaceCalculator() {
  const [surface, setSurface] = useState("");
  const [thickness, setThickness] = useState("4"); // épaisseur en cm, défaut 4 cm
  const [type, setType] = useState("sachets");

  const s = parseFloat(surface) || 0;
  const t = Math.max(parseFloat(thickness) || 4, 4); // minimum 4 cm
  const vol = parseFloat((s * t / 100).toFixed(2)); // m³
  const sachets = Math.ceil(vol / SACHET_VOLUME);
  const hasValue = s > 0;

  return (
    <div className="rounded-2xl overflow-hidden" style={{ border: "1px solid #d8d1c4" }} data-testid="surface-calculator">
      <div className="px-6 py-4" style={{ backgroundColor: "#2d5016" }}>
        <div className="flex items-center gap-2">
          <Calculator size={20} style={{ color: "#b57a3e" }} />
          <h3 className="font-display text-xl" style={{ color: "#f5f2ec" }}>Calculateur de surface</h3>
        </div>
        <p className="text-xs mt-1 opacity-70" style={{ color: "#eae4d8", fontFamily: "'Satoshi', sans-serif" }}>
          Estimez les quantités nécessaires pour votre surface
        </p>
      </div>
      <div className="p-6" style={{ backgroundColor: "white" }}>
        <div className="space-y-4">
          {/* Product type selector */}
          <div>
            <label className="block text-sm font-body mb-2" style={{ color: "#3a2b24", fontWeight: 600 }}>
              Format souhaité
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: "sachets", label: "Sachets 120L" },
                { value: "vrac", label: "Paille en vrac" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setType(opt.value)}
                  className="py-2.5 rounded-md text-sm font-body font-500 transition-all"
                  style={{
                    backgroundColor: type === opt.value ? "#2d5016" : "#eae4d8",
                    color: type === opt.value ? "white" : "#3a2b24",
                    border: type === opt.value ? "1px solid #2d5016" : "1px solid #d8d1c4",
                  }}
                  data-testid={`calc-type-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Surface + épaisseur inputs */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="calc-surface" className="block text-sm font-body mb-2" style={{ color: "#3a2b24", fontWeight: 600 }}>
                Surface à couvrir (m²)
              </label>
              <input
                id="calc-surface"
                type="number"
                value={surface}
                onChange={(e) => setSurface(e.target.value)}
                placeholder="Ex: 100"
                min="0"
                className="input-warm"
                data-testid="calc-surface-input"
              />
            </div>
            <div>
              <label htmlFor="calc-thickness" className="block text-sm font-body mb-2" style={{ color: "#3a2b24", fontWeight: 600 }}>
                Épaisseur (cm) <span style={{ color: "#b57a3e", fontWeight: 400 }}>min. 4 cm</span>
              </label>
              <input
                id="calc-thickness"
                type="number"
                value={thickness}
                onChange={(e) => setThickness(e.target.value)}
                placeholder="4"
                min="4"
                className="input-warm"
                data-testid="calc-thickness-input"
              />
            </div>
          </div>

          {t < 4 && (
            <p className="text-xs px-3 py-2 rounded-md" style={{ backgroundColor: "#fef3e2", color: "#b57a3e", fontFamily: "'Satoshi', sans-serif" }}>
              Épaisseur minimum recommandée : 4 cm pour une efficacité optimale.
            </p>
          )}

          {/* Results */}
          {hasValue && (
            <div className="rounded-xl p-4 space-y-3" style={{ backgroundColor: "#eae4d8", border: "1px solid #d8d1c4" }} data-testid="calc-results">
              {type === "sachets" && (
                <>
                  <div className="flex justify-between items-center pb-2" style={{ borderBottom: "1px solid #d8d1c4" }}>
                    <span className="text-sm font-body" style={{ color: "#3a2b24", opacity: 0.7 }}>Volume nécessaire ({t} cm)</span>
                    <span className="font-display text-xl" style={{ color: "#2d5016" }}>{vol} m³</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-body" style={{ color: "#3a2b24", opacity: 0.7 }}>Sachets 120L nécessaires</span>
                    <span className="font-display text-2xl" style={{ color: "#2d5016" }}>{sachets}</span>
                  </div>
                  <p className="text-xs" style={{ color: "#3a2b24", opacity: 0.5, fontFamily: "'Satoshi', sans-serif" }}>
                    Épaisseur : {t} cm · 1 sachet 120L = 0,12 m³ · Minimum recommandé : 4 cm
                  </p>
                </>
              )}
              {type === "vrac" && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-body" style={{ color: "#3a2b24", opacity: 0.7 }}>Volume en vrac nécessaire ({t} cm)</span>
                    <span className="font-display text-2xl" style={{ color: "#2d5016" }}>{vol} m³</span>
                  </div>
                  <p className="text-xs" style={{ color: "#3a2b24", opacity: 0.5, fontFamily: "'Satoshi', sans-serif" }}>
                    Épaisseur : {t} cm · Minimum recommandé : 4 cm · Contactez-nous pour un devis
                  </p>
                </>
              )}
              <p className="text-xs font-body" style={{ color: "#b57a3e", fontFamily: "'Satoshi', sans-serif" }}>
                Contactez-nous pour obtenir un devis personnalisé.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", product_interest: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useSEO({
    title: "Contact et devis gratuit | Miscanthus Sarthe",
    description: "Demandez un devis gratuit pour rhizomes ou paille de miscanthus : réponse sous 48h. Livraison en Sarthe et Pays de la Loire, retrait à Torcé-en-Vallée.",
    path: "/contact",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
      setForm({ name: "", email: "", phone: "", subject: "", message: "", product_interest: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || "Une erreur s'est produite. Veuillez réessayer ou nous contacter par email.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page" style={{ backgroundColor: "#f5f2ec" }}>
      {/* Header */}
      <div className="page-header" style={{ paddingTop: "7rem" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="section-divider mb-4" />
          <h1 className="font-display text-5xl sm:text-6xl mb-5" style={{ color: "#3a2b24" }}>Contact & Devis</h1>
          <p className="text-base max-w-2xl" style={{ color: "#3a2b24", opacity: 0.7, fontFamily: "'Satoshi', sans-serif" }}>
            Demandez un devis ou posez-nous vos questions. Nous répondons sous 48h.
          </p>
        </div>
      </div>

      {/* Main content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Left: Calculator + Info */}
            <div className="space-y-8">
              <SurfaceCalculator />

              {/* Contact info */}
              <div className="rounded-xl p-6" style={{ backgroundColor: "white", border: "1px solid #d8d1c4" }}>
                <h3 className="font-display text-xl mb-4" style={{ color: "#3a2b24" }}>Nos coordonnées</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <MapPin size={18} style={{ color: "#b57a3e", marginTop: "2px", flexShrink: 0 }} />
                    <div>
                      <p className="font-body font-600 text-sm" style={{ color: "#3a2b24" }}>EARL Torcé Biomasse Energie</p>
                      <p className="text-sm" style={{ color: "#3a2b24", opacity: 0.7, fontFamily: "'Satoshi', sans-serif" }}>Torcé en Vallée — Sarthe (72)<br />Pays de la Loire</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail size={18} style={{ color: "#b57a3e", flexShrink: 0 }} />
                    <a href="mailto:contact@miscanthus-sarthe.fr" className="text-sm transition-opacity hover:opacity-70" style={{ color: "#2d5016", fontFamily: "'Satoshi', sans-serif" }}>
                      contact@miscanthus-sarthe.fr
                    </a>
                  </div>
                </div>
                <div className="mt-4 pt-4 text-xs" style={{ borderTop: "1px solid #d8d1c4", color: "#3a2b24", opacity: 0.6, fontFamily: "'Satoshi', sans-serif" }}>
                  Réponse sous 48h · Devis gratuit · Livraison en Sarthe & PDL
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div className="lg:col-span-2">
              {success ? (
                <div className="rounded-2xl p-12 text-center" style={{ backgroundColor: "white", border: "1px solid #d8d1c4" }} data-testid="contact-success">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: "#eae4d8" }}>
                    <Send size={28} style={{ color: "#2d5016" }} />
                  </div>
                  <h2 className="font-display text-3xl mb-3" style={{ color: "#3a2b24" }}>Message envoyé !</h2>
                  <p className="text-sm mb-6" style={{ color: "#3a2b24", opacity: 0.7, fontFamily: "'Satoshi', sans-serif" }}>
                    Merci pour votre message. Nous vous répondrons dans les plus brefs délais.
                  </p>
                  <button onClick={() => setSuccess(false)} className="btn-primary px-6 py-3 rounded-md font-body font-500 text-sm" data-testid="contact-new-message">
                    Envoyer un autre message
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="rounded-2xl p-8"
                  style={{ backgroundColor: "white", border: "1px solid #d8d1c4" }}
                  data-testid="contact-form"
                >
                  <h2 className="font-display text-2xl mb-6" style={{ color: "#3a2b24" }}>Votre demande</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-body mb-1.5" style={{ color: "#3a2b24", fontWeight: 600 }}>
                        Nom et prénom *
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        placeholder="Jean Dupont"
                        className="input-warm"
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-body mb-1.5" style={{ color: "#3a2b24", fontWeight: 600 }}>
                        Email *
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        placeholder="jean@exemple.fr"
                        className="input-warm"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="contact-phone" className="block text-sm font-body mb-1.5" style={{ color: "#3a2b24", fontWeight: 600 }}>
                        Téléphone
                      </label>
                      <input
                        id="contact-phone"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="06 12 34 56 78"
                        className="input-warm"
                        data-testid="contact-phone"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-product" className="block text-sm font-body mb-1.5" style={{ color: "#3a2b24", fontWeight: 600 }}>
                        Produit d'intérêt
                      </label>
                      <select
                        id="contact-product"
                        name="product_interest"
                        value={form.product_interest}
                        onChange={handleChange}
                        className="input-warm"
                        data-testid="contact-product"
                      >
                        <option value="">Sélectionner...</option>
                        <option value="rhizomes">Rhizomes de Miscanthus</option>
                        <option value="paille-vrac">Paille en vrac</option>
                        <option value="sachet-120l">Sachet 120L</option>
                        <option value="renseignement">Renseignement général</option>
                      </select>
                    </div>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="contact-subject" className="block text-sm font-body mb-1.5" style={{ color: "#3a2b24", fontWeight: 600 }}>
                      Sujet
                    </label>
                    <input
                      id="contact-subject"
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      placeholder="Demande de devis, question technique..."
                      className="input-warm"
                      data-testid="contact-subject"
                    />
                  </div>
                  <div className="mb-6">
                    <label htmlFor="contact-message" className="block text-sm font-body mb-1.5" style={{ color: "#3a2b24", fontWeight: 600 }}>
                      Message *
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      required
                      rows={form.product_interest === "rhizomes" ? 8 : 5}
                      placeholder={form.product_interest === "rhizomes"
                        ? "Surface à planter (m²) : \nUtilisation souhaitée (production de paille, chasse, énergie...) : \nType de sol (argileux, limoneux, sableux…) : \nAutres informations ou questions :"
                        : "Décrivez votre projet, votre surface à pailler, vos questions…"}
                      className="input-warm resize-none"
                      data-testid="contact-message"
                    />
                  </div>
                  {error && (
                    <p className="text-sm mb-4 px-3 py-2 rounded-md" style={{ color: "#dc2626", backgroundColor: "#fef2f2", fontFamily: "'Satoshi', sans-serif" }} data-testid="contact-error">
                      {error}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary w-full py-3.5 rounded-md font-body font-500 text-base flex items-center justify-center gap-2 disabled:opacity-60"
                    data-testid="contact-submit"
                  >
                    {submitting ? "Envoi en cours..." : <><Send size={18} /> Envoyer le message</>}
                  </button>
                  <p className="text-xs mt-3 text-center" style={{ color: "#3a2b24", opacity: 0.5, fontFamily: "'Satoshi', sans-serif" }}>
                    Vos données sont utilisées uniquement pour répondre à votre demande.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
